/**
 * DiagramHost Web Component
 *
 * A component that displays diagrams using diagrams.net viewer with integrated
 * Azure Blob Storage backend. Diagrams are loaded from and saved to Azure Blob Storage
 * using Azure AD authentication via the Quantum Auth Service.
 * Users can edit diagrams in a popup window with automatic save to storage.
 *
 * Uses diagrams.net client mode for postMessage communication with editor popup.
 *
 * @element qds-diagram-host
 *
 * @attr {String} diagram - XML content of the diagram (optional if using storage)
 * @attr {Boolean} editable - Whether the diagram can be edited (default: true)
 * @attr {String} storage-account - Azure storage account name (default: "qdqedprodqvc")
 * @attr {String} container - Blob container name (default: "qed-prod-diagrams")
 * @attr {String} path - Storage path (forward-slash delimited, e.g., "engineering/widget-x/assembly")
 * @attr {String} drawing-name - Diagram filename (extension optional; defaults to .drawio if not provided)
 * @attr {String} libraries - Comma-separated list of custom library names to load (default: "Quantum")
 * @attr {String} width - Width of the diagram container (default: 100%)
 * @attr {String} height - Height of the diagram container (default: 600px)
 *
 * @property {Boolean} editable - Enables or disables the diagrams.net editing controls.
 *
 * @fires diagram-loaded - When diagram is loaded from storage
 * @fires diagram-saved - When diagram is saved to storage
 * @fires diagram-error - When an error occurs
 * @fires diagram-change - When the diagram content changes
 *
 * @requires Authentication - Requires Quantum Auth Service to be initialized for storage access
 * @requires Azure AD Permission - Requires 'https://storage.azure.com/user_impersonation' scope
 * @requires Azure RBAC - User must have 'Storage Blob Data Contributor' or 'Storage Blob Data Reader' role
 */

import { loadBaseCSS } from '../shared/button-utils.js';

loadBaseCSS();

interface DiagramMessage {
  event?: string;
  xml?: string;
  data?: string;
  action?: string;
  message?: any;
  [key: string]: any;
}

interface DiagramEventDetail {
  diagram?: string;
  error?: string;
  path?: string;
}

class DiagramHost extends HTMLElement {
  private shadow: ShadowRoot;
  private _diagramXml: string;
  private _width: string;
  private _height: string;
  private _viewerIframe: HTMLIFrameElement | null;
  private _containerDiv: HTMLDivElement | null;
  private _loadingIndicator: HTMLDivElement | null;
  private _errorMessage: HTMLDivElement | null;
  private _editButton: HTMLButtonElement | null;
  private _fullscreenButton: HTMLButtonElement | null;
  private _exitFullscreenButton: HTMLButtonElement | null;
  private _fullscreenOverlay: HTMLDivElement | null;
  private _editable: boolean;
  private _isFullscreen: boolean;

  // Storage properties
  private _storageAccount: string;
  private _containerName: string;
  private _path: string;
  private _drawingName: string;

  // Library properties
  private _libraries: string;

  // Format detection
  private _diagramFormat: string;

  // Window communication
  private _editorWindow: Window | null;
  private _handleMessage: (event: MessageEvent) => void;
  private _handleBeforeUnload: () => void;
  private _handleEditClick: () => void;
  private _handleFullscreenClick: () => void;
  private _handleExitFullscreenClick: () => void;

  constructor() {
    super();

    this.shadow = this.attachShadow({ mode: 'open' });

    // Default empty diagram
    this._diagramXml = '<mxGraphModel dx="879" dy="557" grid="1" gridSize="10" guides="1" tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" pageWidth="850" pageHeight="1100" math="0" shadow="0"><root><mxCell id="0"/><mxCell id="1" parent="0"/></root></mxGraphModel>';
    this._width = '100%';
    this._height = '600px';
    this._viewerIframe = null;
    this._containerDiv = null;
    this._loadingIndicator = null;
    this._errorMessage = null;
    this._editButton = null;
    this._fullscreenButton = null;
    this._exitFullscreenButton = null;
    this._fullscreenOverlay = null;
    this._editable = true;
    this._isFullscreen = false;
    this._editorWindow = null;

    // Storage properties
    this._storageAccount = 'qdqedprodqvc';  // Default Quantum storage account
    this._containerName = 'qed-prod-diagrams';  // Default Quantum diagrams container
    this._path = '';
    this._drawingName = '';

    // Library properties
    this._libraries = 'Quantum';  // Default to Quantum library

    // Format detection
    this._diagramFormat = 'xml';

    // Bind event handlers
    this._handleMessage = this._processMessage.bind(this);
    this._handleBeforeUnload = this._onBeforeUnload.bind(this);
    this._handleEditClick = this._onEditClick.bind(this);
    this._handleFullscreenClick = this._enterFullscreen.bind(this);
    this._handleExitFullscreenClick = this._exitFullscreen.bind(this);

    this._render();
  }

  static get observedAttributes(): string[] {
    return [
      'diagram',
      'width',
      'height',
      'editable',
      'storage-account',
      'container',
      'path',
      'drawing-name',
      'libraries'
    ];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'diagram':
        this._diagramXml = newValue || this._diagramXml;
        this._updateViewerContent();
        break;
      case 'width':
        this._width = newValue || '100%';
        this._updateContainerSize();
        break;
      case 'height':
        this._height = newValue || '600px';
        this._updateContainerSize();
        break;
      case 'editable':
        this._editable = newValue === 'true' || newValue === '';
        this._updateEditButtonVisibility();
        break;
      case 'storage-account':
        this._storageAccount = newValue || 'qdqedprodqvc';
        break;
      case 'container':
        this._containerName = newValue || 'qed-prod-diagrams';
        break;
      case 'path':
        this._path = newValue || '';
        break;
      case 'drawing-name':
        this._drawingName = newValue || '';
        // Update format when drawing name changes
        this._diagramFormat = this._getFileFormat();
        // Trigger load if we now have complete storage config and are connected
        if (this.isConnected && this._hasStorageConfig()) {
          console.log('[qds-diagram-host] Drawing name set, loading from storage:', {
            storageAccount: this._storageAccount,
            container: this._containerName,
            path: this._path,
            drawingName: this._drawingName
          });
          // Check if auth is ready
          const getQuantumAuth = (window as any).getQuantumAuth;
          const authService = getQuantumAuth?.();
          if (authService) {
            this._loadFromStorage();
          } else {
            // Wait for auth
            console.log('[qds-diagram-host] Waiting for auth to load diagram');
            const handleAuthInit = () => {
              this._loadFromStorage();
              document.removeEventListener('quantum-auth-initialized', handleAuthInit);
            };
            document.addEventListener('quantum-auth-initialized', handleAuthInit);
          }
        }
        break;
      case 'libraries':
        this._libraries = newValue || 'Quantum';
        break;
    }
  }

  connectedCallback(): void {
    if (!this.isConnected) return;

    window.addEventListener('message', this._handleMessage);
    window.addEventListener('beforeunload', this._handleBeforeUnload);

    // Load diagram from storage if configured
    if (this._hasStorageConfig()) {
      // Check if auth is already available
      const getQuantumAuth = (window as any).getQuantumAuth;
      const authService = getQuantumAuth?.();

      if (authService) {
        // Auth is ready, load immediately
        this._loadFromStorage();
      } else {
        // Auth not ready yet, wait for it
        console.log('Waiting for auth service to initialize before loading diagram...');
        const handleAuthInit = () => {
          console.log('Auth initialized, loading diagram from storage');
          this._loadFromStorage();
          document.removeEventListener('quantum-auth-initialized', handleAuthInit);
        };
        document.addEventListener('quantum-auth-initialized', handleAuthInit);
      }
    }
  }

  disconnectedCallback(): void {
    window.removeEventListener('message', this._handleMessage);
    window.removeEventListener('beforeunload', this._handleBeforeUnload);

    if (this._editButton) {
      this._editButton.removeEventListener('click', this._handleEditClick);
    }

    if (this._fullscreenButton) {
      this._fullscreenButton.removeEventListener('click', this._handleFullscreenClick);
    }

    if (this._exitFullscreenButton) {
      this._exitFullscreenButton.removeEventListener('click', this._handleExitFullscreenClick);
    }

    if (this._editorWindow && !this._editorWindow.closed) {
      this._editorWindow.close();
    }
  }

  get diagram(): string {
    return this._diagramXml;
  }

  set diagram(value: string) {
    this.setAttribute('diagram', value);
  }

  get editable(): boolean {
    return this._editable;
  }

  set editable(value: boolean) {
    this._editable = Boolean(value);
    this.toggleAttribute('editable', this._editable);
    this._updateEditButtonVisibility();
  }

  private _render(): void {
    const styles = `
      :host {
        display: block;
        --_diagram-host-surface: var(--theme-background-elevated, var(--theme-background-primary));
        --_diagram-host-overlay: var(--theme-background-overlay);
        --_diagram-host-overlay-subtle: var(--theme-background-overlay-subtle);
        --_diagram-host-border: var(--theme-border-primary);
        --_diagram-host-text: var(--theme-text-primary);
        --_diagram-host-subtle-text: var(--theme-text-secondary);
        --_diagram-host-accent: var(--theme-accent-primary);
        --_diagram-host-error: var(--theme-error);
        --_diagram-host-error-surface: var(--theme-background-error-subtle);
        --_diagram-host-shadow-xs: var(--theme-shadow-xs);
        --_diagram-host-shadow-sm: var(--theme-shadow-sm);
        --_diagram-host-shadow-md: var(--theme-shadow-md);
        --_diagram-host-radius-sm: var(--theme-radius-sm, 4px);
        --_diagram-host-radius-md: var(--theme-radius-md, 8px);
        position: relative;
        color: var(--_diagram-host-text);
        font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
      }

      .diagram-container {
        position: relative;
        width: 100%;
        height: 100%;
        background: var(--_diagram-host-surface);
        border-radius: var(--_diagram-host-radius-sm);
        overflow: hidden;
      }

      iframe {
        width: 100%;
        height: 100%;
        border: none;
        display: block;
      }

      .loading-indicator {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: var(--_diagram-host-overlay);
        color: var(--_diagram-host-text);
        padding: var(--theme-space-5, 20px) var(--theme-space-6, 24px);
        border-radius: var(--_diagram-host-radius-md);
        box-shadow: var(--_diagram-host-shadow-md);
        display: none;
        align-items: center;
        gap: var(--theme-space-3, 12px);
        z-index: 100;
      }

      .loading-indicator.visible {
        display: flex;
      }

      .spinner {
        width: 20px;
        height: 20px;
        border: 2px solid var(--_diagram-host-border);
        border-top-color: var(--_diagram-host-accent);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      .error-message {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: var(--_diagram-host-error-surface);
        padding: var(--theme-space-5, 20px);
        border-radius: var(--_diagram-host-radius-md);
        border: 1px solid var(--_diagram-host-error);
        color: var(--_diagram-host-error);
        max-width: 400px;
        display: none;
        z-index: 100;
      }

      .error-message.visible {
        display: block;
      }

      .error-message h3 {
        margin: 0 0 8px 0;
        font-size: 16px;
      }

      .error-message p {
        margin: 0;
        font-size: var(--theme-font-size-sm, 14px);
        color: var(--_diagram-host-subtle-text);
      }

      /* Shared button style for icon buttons */
      .icon-button {
        position: absolute;
        top: var(--theme-space-3, 12px);
        z-index: 50;
        width: 32px;
        height: 32px;
        padding: 0;
        background: var(--_diagram-host-overlay-subtle);
        color: var(--_diagram-host-subtle-text);
        border: 1px solid var(--_diagram-host-border);
        border-radius: var(--_diagram-host-radius-sm);
        cursor: pointer;
        box-shadow: var(--_diagram-host-shadow-xs);
        transition:
          background-color 0.2s ease,
          border-color 0.2s ease,
          color 0.2s ease,
          box-shadow 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .icon-button:hover {
        background: var(--_diagram-host-surface);
        color: var(--_diagram-host-accent);
        border-color: var(--_diagram-host-accent);
        box-shadow: var(--_diagram-host-shadow-sm);
      }

      .icon-button:focus-visible {
        outline: 2px solid var(--_diagram-host-accent);
        outline-offset: 2px;
        box-shadow: 0 0 0 4px var(--button-focus-ring);
      }

      .icon-button svg {
        width: 18px;
        height: 18px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.5;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      .fullscreen-button {
        right: 52px;
      }

      .edit-button {
        right: var(--theme-space-3, 12px);
      }

      .fullscreen-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: var(--_diagram-host-surface);
        z-index: 10000;
        display: none;
      }

      .fullscreen-overlay.visible {
        display: block;
      }

      .fullscreen-container {
        width: 100%;
        height: 100%;
        position: relative;
      }

      .fullscreen-container iframe {
        width: 100%;
        height: 100%;
        border: none;
      }

      .exit-fullscreen-button {
        position: absolute;
        top: var(--theme-space-4, 16px);
        right: var(--theme-space-4, 16px);
        z-index: 100;
        padding: 10px;
        background: var(--_diagram-host-overlay);
        color: var(--_diagram-host-subtle-text);
        border: 1px solid var(--_diagram-host-border);
        border-radius: var(--_diagram-host-radius-sm);
        cursor: pointer;
        box-shadow: var(--_diagram-host-shadow-sm);
        transition:
          background-color 0.2s ease,
          border-color 0.2s ease,
          color 0.2s ease,
          box-shadow 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .exit-fullscreen-button:hover {
        background: var(--_diagram-host-surface);
        color: var(--_diagram-host-accent);
        border-color: var(--_diagram-host-accent);
        box-shadow: var(--_diagram-host-shadow-md);
      }

      .exit-fullscreen-button:focus-visible {
        outline: 2px solid var(--_diagram-host-accent);
        outline-offset: 2px;
        box-shadow: 0 0 0 4px var(--button-focus-ring);
      }

      .exit-fullscreen-button svg {
        width: 20px;
        height: 20px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.5;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
    `;

    this.shadow.innerHTML = `
      <style>${styles}</style>
      <div class="diagram-container" part="container">
        <button class="icon-button fullscreen-button" part="fullscreen-button" aria-label="View Fullscreen">
          <svg viewBox="0 0 24 24">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
          </svg>
        </button>
        <button class="icon-button edit-button" part="edit-button" aria-label="Edit Diagram">
          <svg viewBox="0 0 24 24">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>
        <div class="loading-indicator" part="loading">
          <div class="spinner"></div>
          <span>Loading diagram...</span>
        </div>
        <div class="error-message" part="error">
          <h3>Error Loading Diagram</h3>
          <p></p>
        </div>
      </div>
      <div class="fullscreen-overlay" part="fullscreen-overlay">
        <div class="fullscreen-container">
          <button class="exit-fullscreen-button" part="exit-fullscreen-button" aria-label="Exit Fullscreen">
            <svg viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Store references
    this._containerDiv = this.shadow.querySelector('.diagram-container');
    this._loadingIndicator = this.shadow.querySelector('.loading-indicator');
    this._errorMessage = this.shadow.querySelector('.error-message');
    this._editButton = this.shadow.querySelector('.edit-button');
    this._fullscreenButton = this.shadow.querySelector('.fullscreen-button');
    this._exitFullscreenButton = this.shadow.querySelector('.exit-fullscreen-button');
    this._fullscreenOverlay = this.shadow.querySelector('.fullscreen-overlay');

    // Add edit button click handler
    if (this._editButton) {
      this._editButton.addEventListener('click', this._handleEditClick);
    }

    // Add fullscreen button click handler
    if (this._fullscreenButton) {
      this._fullscreenButton.addEventListener('click', this._handleFullscreenClick);
    }

    // Add exit fullscreen button click handler
    if (this._exitFullscreenButton) {
      this._exitFullscreenButton.addEventListener('click', this._handleExitFullscreenClick);
    }

    // Initialize viewer
    this._renderViewer();
    this._updateContainerSize();
    this._updateEditButtonVisibility();
  }

  private _updateEditButtonVisibility(): void {
    if (this._editButton) {
      this._editButton.style.display = this._editable ? 'block' : 'none';
    }
  }

  private _renderViewer(): void {
    const container = this._containerDiv;
    if (!container) return;

    // Remove existing viewer if any
    if (this._viewerIframe) {
      this._viewerIframe.remove();
    }

    // Create viewer iframe with popup support
    this._viewerIframe = document.createElement('iframe');

    // Enable popups and scripts for diagrams.net client mode
    this._viewerIframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox');

    // Use diagrams.net viewer (without edit button since we have our own)
    const viewerUrl = new URL('https://viewer.diagrams.net/');
    viewerUrl.searchParams.set('highlight', '0000ff');
    viewerUrl.searchParams.set('layers', '1');
    viewerUrl.searchParams.set('nav', '1');
    viewerUrl.searchParams.set('title', 'diagram');
    viewerUrl.searchParams.set('page-fit', '1');  // Auto-zoom to fit content
    viewerUrl.hash = 'R' + encodeURIComponent(this._diagramXml);

    this._viewerIframe.src = viewerUrl.toString();

    container.appendChild(this._viewerIframe);
  }

  private _updateViewerContent(): void {
    if (!this._viewerIframe) {
      this._renderViewer();
      return;
    }

    // Reload viewer with new diagram content
    const viewerUrl = new URL('https://viewer.diagrams.net/');
    viewerUrl.searchParams.set('highlight', '0000ff');
    viewerUrl.searchParams.set('lightbox', '0')
    //viewerUrl.searchParams.set('layers', '1');
    //viewerUrl.searchParams.set('nav', '1');
    viewerUrl.searchParams.set('title', 'diagram');
    //viewerUrl.searchParams.set('page-fit', '1');  // Auto-zoom to fit content
    viewerUrl.hash = 'R' + encodeURIComponent(this._diagramXml);

    this._viewerIframe.src = viewerUrl.toString();
  }

  /**
   * Constructs the clibs parameter for custom libraries
   * Libraries are always loaded from the CDN (not localhost) because diagrams.net
   * editor needs to fetch them, and it can't reach localhost URLs
   * @returns String with encoded library URLs prefixed with 'U' and separated by semicolons
   */
  private _getLibrariesParameter(): string {
    if (!this._libraries) return '';

    const libraryNames = this._libraries.split(',').map(name => name.trim()).filter(name => name);
    if (libraryNames.length === 0) return '';

    // Always use CDN URL - diagrams.net editor needs to fetch the library files
    const baseUrl = 'https://cdn.q360.ai/assets/components/diagram-host/libraries/';

    // Build library URLs: each URL is prefixed with 'U' and separated by semicolons
    // We need to URL encode the entire URL for the clibs parameter
    const libraryUrls = libraryNames.map(name => {
      const fileName = name.endsWith('.xml') ? name : `${name}.xml`;
      const fullUrl = `${baseUrl}${fileName}`;
      return `U${encodeURIComponent(fullUrl)}`;
    });

    return libraryUrls.join(';');
  }

  private _onEditClick(): void {
    console.log('Edit button clicked, opening editor popup...');

    // Close existing editor window if open
    if (this._editorWindow && !this._editorWindow.closed) {
      this._editorWindow.close();
    }

    // Build editor URL with client mode enabled
    const editorUrl = new URL('https://embed.diagrams.net/');
    editorUrl.searchParams.set('client', '1');
    editorUrl.searchParams.set('embed', '1');
    editorUrl.searchParams.set('proto', 'json');
    editorUrl.searchParams.set('spin', '1');
    editorUrl.searchParams.set('modified', 'unsavedChanges');
    editorUrl.searchParams.set('noSaveBtn', '1');
    editorUrl.searchParams.set('border', '0');  // No border/margin on exports

    // Set format based on file extension (important for .drawio.svg files)
    if (this._diagramFormat !== 'xml') {
      editorUrl.searchParams.set('format', this._diagramFormat);
      console.log(`Setting editor format to: ${this._diagramFormat}`);
    }

    // Add custom libraries if specified
    // Note: We manually append the clibs parameter to avoid double URL encoding
    const librariesParam = this._getLibrariesParameter();
    let finalUrl = editorUrl.toString();
    if (librariesParam) {
      finalUrl += `&clibs=${librariesParam}`;
      console.log('Loading custom libraries:', librariesParam);
    }

    console.log('Opening editor at:', finalUrl);

    // Open popup window
    this._editorWindow = window.open(
      finalUrl,
      'diagramEditor',
      'width=1200,height=800,resizable=yes,scrollbars=yes'
    );

    if (!this._editorWindow) {
      console.error('Failed to open editor window - popup blocked?');
      this._showError('Failed to open editor. Please allow popups for this site.');
    } else {
      console.log('Editor window opened successfully');
    }
  }

  private _updateContainerSize(): void {
    if (this._containerDiv) {
      this._containerDiv.style.width = this._width;
      this._containerDiv.style.height = this._height;
    }
  }

  private _showLoading(message: string = 'Loading diagram...'): void {
    if (this._loadingIndicator) {
      const span = this._loadingIndicator.querySelector('span');
      if (span) span.textContent = message;
      this._loadingIndicator.classList.add('visible');
    }
  }

  private _hideLoading(): void {
    if (this._loadingIndicator) {
      this._loadingIndicator.classList.remove('visible');
    }
  }

  private _showError(message: string): void {
    if (this._errorMessage) {
      const p = this._errorMessage.querySelector('p');
      if (p) p.textContent = message;
      this._errorMessage.classList.add('visible');
    }

    this.dispatchEvent(new CustomEvent<DiagramEventDetail>('diagram-error', {
      detail: { error: message },
      bubbles: true,
      composed: true
    }));
  }

  private _hideError(): void {
    if (this._errorMessage) {
      this._errorMessage.classList.remove('visible');
    }
  }

  private _hasStorageConfig(): boolean {
    // Path can be empty string for root-level files, so check for undefined/null instead
    return !!(
      this._storageAccount &&
      this._containerName &&
      this._path !== undefined &&
      this._path !== null &&
      this._drawingName
    );
  }

  /**
   * Get storage access token from the auth service
   * @returns Promise with the access token
   */
  private async _getStorageToken(): Promise<string> {
    // Check if auth service is available
    const getQuantumAuth = (window as any).getQuantumAuth;
    if (!getQuantumAuth || typeof getQuantumAuth !== 'function') {
      throw new Error('Authentication service not available. Please ensure authentication is initialized.');
    }

    const authService = getQuantumAuth();
    if (!authService) {
      throw new Error('Authentication service not initialized. Please log in first.');
    }

    // Request token with Azure Storage scope
    try {
      const token = await authService.getToken(['https://storage.azure.com/user_impersonation']);
      return token;
    } catch (error) {
      console.error('Failed to get storage token:', error);
      throw new Error('Failed to get storage access token. Please ensure you are logged in and have the required permissions.');
    }
  }

  private _getStoragePath(): string {
    // If drawing name already has an extension, use it as-is; otherwise append .drawio
    const filename = this._drawingName.includes('.')
      ? this._drawingName
      : `${this._drawingName}.drawio`;

    // If path is empty (root level), just return filename
    // Otherwise join path and filename with a slash
    return this._path ? `${this._path}/${filename}` : filename;
  }

  /**
   * Detect diagram format from filename
   * @returns Format string for diagrams.net editor (svg, xml, png, etc.)
   */
  private _getFileFormat(): string {
    const filename = this._drawingName.toLowerCase();

    // Check for SVG format (including .drawio.svg)
    if (filename.endsWith('.svg') || filename.endsWith('.drawio.svg')) {
      return 'svg';
    }

    // Check for PNG format (including .drawio.png)
    if (filename.endsWith('.png') || filename.endsWith('.drawio.png')) {
      return 'png';
    }

    // Check for HTML format
    if (filename.endsWith('.html')) {
      return 'html';
    }

    // Default to XML format (.drawio, .xml, or no extension)
    return 'xml';
  }

  private _getStorageUrl(): string {
    const path = this._getStoragePath();
    return `https://${this._storageAccount}.blob.core.windows.net/${this._containerName}/${path}`;
  }

  private async _loadFromStorage(): Promise<void> {
    if (!this._hasStorageConfig()) {
      console.warn('[qds-diagram-host] Storage configuration incomplete, skipping load');
      // Still render with empty diagram
      this._updateViewerContent();
      return;
    }

    console.log('[qds-diagram-host] Loading diagram from storage');
    this._showLoading('Loading diagram from storage...');
    this._hideError();

    try {
      console.log('[qds-diagram-host] Getting storage token...');
      const token = await this._getStorageToken();
      const url = this._getStorageUrl();
      console.log('[qds-diagram-host] Fetching from:', url);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-ms-version': '2020-04-08'
        }
      });
      console.log('[qds-diagram-host] Response status:', response.status);

      if (response.ok) {
        const diagramXml = await response.text();
        this._diagramXml = diagramXml;
        // Detect and store format based on filename
        this._diagramFormat = this._getFileFormat();
        this._updateViewerContent();

        this.dispatchEvent(new CustomEvent<DiagramEventDetail>('diagram-loaded', {
          detail: {
            diagram: diagramXml,
            path: this._getStoragePath()
          },
          bubbles: true,
          composed: true
        }));
      } else if (response.status === 404) {
        // Diagram doesn't exist yet - use empty diagram
        console.log('Diagram not found in storage, using empty diagram');
        // Keep the default empty diagram and render viewer
        this._updateViewerContent();
      } else {
        throw new Error(`Failed to load diagram: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error loading diagram';
      console.error('Error loading diagram from storage:', error);
      // On error, fall back to empty diagram
      console.log('Using empty diagram as fallback');
      this._updateViewerContent();
      this._showError(message);
    } finally {
      this._hideLoading();
    }
  }

  private async _saveToStorage(diagramXml: string): Promise<void> {
    if (!this._hasStorageConfig()) {
      console.warn('Storage configuration incomplete, skipping save');
      return;
    }

    this._showLoading('Saving diagram to storage...');
    this._hideError();

    try {
      const token = await this._getStorageToken();
      const url = this._getStorageUrl();
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-ms-version': '2020-04-08',
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'application/xml'
        },
        body: diagramXml
      });

      if (!response.ok) {
        throw new Error(`Failed to save diagram: ${response.status} ${response.statusText}`);
      }

      // Storage save succeeded
      console.log('Diagram saved to storage successfully');

      this.dispatchEvent(new CustomEvent<DiagramEventDetail>('diagram-saved', {
        detail: {
          diagram: diagramXml,
          path: this._getStoragePath()
        },
        bubbles: true,
        composed: true
      }));

      this._hideLoading();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error saving diagram';
      console.error('Error saving diagram to storage:', error);
      this._showError(`Storage save failed: ${message}`);
      this._hideLoading();
      // Re-throw so caller knows it failed
      throw error;
    }
  }

  private _processMessage(event: MessageEvent): void {
    // Only process messages from diagrams.net domains
    const origin = event.origin;
    if (!origin.includes('diagrams.net') && !origin.includes('draw.io')) {
      return;
    }

    // Only process messages if this instance has an open editor window OR if it's an init event
    // (init event comes from a popup that was just opened by this instance)
    const isInitEvent = (() => {
      try {
        const msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        return msg.event === 'init';
      } catch {
        return false;
      }
    })();

    // If we don't have an editor window and this isn't an init event, ignore it
    if (!this._editorWindow && !isInitEvent) {
      return;
    }

    // If we have an editor window but the message isn't from it, ignore it
    if (this._editorWindow && event.source !== this._editorWindow && !isInitEvent) {
      return;
    }

    console.log('Message from diagrams.net:', event.data);

    try {
      const msg: DiagramMessage = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

      // Handle different message events from diagrams.net client mode
      if (msg.event === 'init') {
        // Editor is ready and requesting the diagram data
        // Only respond if we just opened an editor (have a window reference)
        if (!this._editorWindow || this._editorWindow.closed) {
          console.log('Ignoring init from editor not opened by this instance');
          return;
        }

        console.log('Editor initialized, sending diagram data...');
        console.log('Diagram XML length:', this._diagramXml?.length || 0);

        // Ensure we have valid diagram XML
        const xmlToSend = this._diagramXml || '<mxGraphModel dx="879" dy="557" grid="1" gridSize="10" guides="1" tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" pageWidth="850" pageHeight="1100" math="0" shadow="0"><root><mxCell id="0"/><mxCell id="1" parent="0"/></root></mxGraphModel>';

        // Send the diagram XML to the editor
        const response = {
          action: 'load',
          xml: xmlToSend,
          autosave: 1
        };

        console.log('Sending load action to editor with XML:', xmlToSend.substring(0, 100) + '...');

        if (this._editorWindow) {
          this._editorWindow.postMessage(JSON.stringify(response), '*');
        }
      } else if (msg.event === 'save') {
        // Editor saved the diagram
        console.log('Diagram saved from editor');
        const diagramXml = msg.xml || msg.data;

        if (diagramXml) {
          // ALWAYS update local diagram first (regardless of storage)
          this._diagramXml = diagramXml;
          this._updateViewerContent();

          this.dispatchEvent(new CustomEvent<DiagramEventDetail>('diagram-change', {
            detail: { diagram: diagramXml },
            bubbles: true,
            composed: true
          }));

          // Try to save to storage if configured (best effort, don't fail if it errors)
          if (this._hasStorageConfig()) {
            this._saveToStorage(diagramXml).catch(error => {
              console.warn('Storage save failed, but diagram updated locally:', error);
            });
          }

          // Send acknowledgment back to editor
          if (this._editorWindow && event.source) {
            const ack = {
              event: 'export',
              format: 'xml',
              message: event.data.message || {}
            };
            (event.source as Window).postMessage(JSON.stringify(ack), '*');
          }

          // Close the editor window after save
          if (this._editorWindow && !this._editorWindow.closed) {
            this._editorWindow.close();
          }
          this._editorWindow = null;
        }
      } else if (msg.event === 'exit') {
        // Editor window closed by user
        console.log('Editor exit requested');
        if (this._editorWindow && !this._editorWindow.closed) {
          this._editorWindow.close();
        }
        this._editorWindow = null;
      } else if (msg.event === 'export') {
        // Handle export if needed
        console.log('Export event received');
      }
    } catch (error) {
      console.error('Error processing message from diagrams.net:', error);
    }
  }

  private _onBeforeUnload(): void {
    // Close editor window if open when parent closes
    if (this._editorWindow && !this._editorWindow.closed) {
      this._editorWindow.close();
    }
  }

  /**
   * Enter fullscreen mode
   * Creates a fullscreen overlay with the diagram viewer
   */
  private _enterFullscreen(): void {
    if (!this._fullscreenOverlay) return;

    // Create a fullscreen viewer iframe
    const fullscreenContainer = this._fullscreenOverlay.querySelector('.fullscreen-container');
    if (!fullscreenContainer) return;

    // Remove any existing iframe in fullscreen
    const existingIframe = fullscreenContainer.querySelector('iframe');
    if (existingIframe) {
      existingIframe.remove();
    }

    // Create new viewer iframe for fullscreen
    const viewerIframe = document.createElement('iframe');
    viewerIframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox');

    // Use same viewer configuration as main viewer
    const viewerUrl = new URL('https://viewer.diagrams.net/');
    viewerUrl.searchParams.set('highlight', '0000ff');
    viewerUrl.searchParams.set('layers', '1');
    viewerUrl.searchParams.set('nav', '1');
    viewerUrl.searchParams.set('title', 'diagram');
    viewerUrl.searchParams.set('page-fit', '1');
    viewerUrl.hash = 'R' + encodeURIComponent(this._diagramXml);

    viewerIframe.src = viewerUrl.toString();
    fullscreenContainer.appendChild(viewerIframe);

    // Show the overlay
    this._fullscreenOverlay.classList.add('visible');
    this._isFullscreen = true;
  }

  /**
   * Exit fullscreen mode
   * Hides the fullscreen overlay and removes the viewer
   */
  private _exitFullscreen(): void {
    if (!this._fullscreenOverlay) return;

    // Hide the overlay
    this._fullscreenOverlay.classList.remove('visible');
    this._isFullscreen = false;

    // Remove the fullscreen iframe
    const fullscreenContainer = this._fullscreenOverlay.querySelector('.fullscreen-container');
    if (fullscreenContainer) {
      const iframe = fullscreenContainer.querySelector('iframe');
      if (iframe) {
        iframe.remove();
      }
    }
  }
}

customElements.define('qds-diagram-host', DiagramHost);

export default DiagramHost;
